import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useServices } from "../../context/ServiceProvider";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  Input,
  Button,
  Typography,
  Tag,
  List,
  Spin,
  Empty,
  Alert,
} from "antd";
import { CopyOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useRecents } from "../../hooks/useRecents";
import {
  resetBreadcrumbs,
  setIsCreating,
  setStep,
} from "../../store/slices/navigationSlice";
import CloneProjectModal from "../home/CloneProjectModal";

export default function ProjectList() {
  const { project } = useServices();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [query, setQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const { record } = useRecents({ storageKey: "RECENTS::v1", maxItems: 50 });
  const dispatch = useDispatch();
  const [showCloneModal, setShowCloneModal] = useState(false);

  // Dropdown search state
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = React.useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await project.fetchAllProjects();
      setRecords(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [project]);

  // Dropdown search with debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    const term = (query || "").trim();
    if (term.length < 3) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await project.searchProjects(term);
        setSearchResults(Array.isArray(data) ? data : []);
        setSearchOpen(true);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, project]);

  useEffect(() => {
    dispatch(resetBreadcrumbs());
  }, [dispatch]);
  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const columns = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "Name",
        key: "Name",
        sorter: (a, b) => a.Name.localeCompare(b.Name),
        width: "75%",
        ellipsis: true,
        render: (text, rec) => (
          <Link
            to={`/p/${rec.Id}`}
            onClick={() => {
              record({
                id: String(rec.Id),
                title: String(text || rec.Name || rec.Id),
                url: `/p/${rec.Id}`,
                type: "Project",
              });
            }}
          >
            {text || rec.Id}
          </Link>
        ),
      },
      {
        title: "Status",
        dataIndex: "Project_Status__c",
        key: "Status",
        sorter: (a, b) =>
          a.Project_Status__c.localeCompare(b.Project_Status__c),
        filters: [
          { text: "Open", value: "Open" },
          { text: "Closed", value: "Closed" },
          { text: "Draft", value: "Draft" },
          { text: "Roster Hold", value: "Roster Hold" },
          { text: "Presale", value: "Presale" },
        ],
        onFilter: (value, record) => record.Project_Status__c === value,
        render: (text, rec) => (
          <Tag
            color={
              rec.Project_Status__c === "Open"
                ? "green"
                : rec.Project_Status__c === "Closed"
                  ? "red"
                  : "blue"
            }
          >
            {text || rec.Project_Status__c}
          </Tag>
        ),
      },
    ],
    [record],
  );

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="flex items-center justify-between mb-4">
        <Typography.Title level={3} style={{ margin: 0 }}>
          Projects
        </Typography.Title>
        <div className="flex items-center gap-3">
          <Button
            type="primary"
            onClick={() => {
              dispatch(setIsCreating(true));
              dispatch(setStep(1));
              navigate("/create");
            }}
            icon={
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            }
          >
            Create
          </Button>
          <Button
            type="default"
            onClick={() => setShowCloneModal(true)}
            icon={<CopyOutlined />}
          >
            Clone Project
          </Button>
          <div className="relative" style={{ maxWidth: 360 }}>
            <Input.Search
              placeholder="Search projects"
              allowClear
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => setSearchOpen(false)}
            />
            {searchOpen && query.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto">
                {searchLoading ? (
                  <div className="p-3 text-sm text-gray-500">
                    <Spin size="small" /> Searching…
                  </div>
                ) : query.length < 3 ? (
                  <div className="p-3 text-sm text-gray-500">
                    Enter at least 3 characters to search
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="p-3">
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No projects found"
                    />
                  </div>
                ) : (
                  <List
                    dataSource={searchResults}
                    size="large"
                    renderItem={(item) => (
                      <List.Item
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        onMouseDown={() => {
                          setQuery("");
                          setSearchResults([]);
                          setSearchOpen(false);
                          record({
                            id: String(item.Id),
                            title: String(item.Name || item.Id),
                            url: `/p/${item.Id}`,
                            type: "Project",
                          });
                          navigate(`/p/${item.Id}`);
                        }}
                      >
                        <div className="w-full">
                          <div className="text-gray-900 text-sm font-medium">
                            {item.Name}
                          </div>
                          <div className="text-xs text-gray-500">{item.Id}</div>
                        </div>
                      </List.Item>
                    )}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Clone Project Modal */}
      <CloneProjectModal
        visible={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        onSuccess={(clonedProject) => {
          setShowCloneModal(false);
          loadAll(); // Refresh the list
        }}
      />
      <Table
        loading={loading}
        rowKey={(r) => r.Id}
        dataSource={records}
        columns={columns}
        scroll={{ y: "calc(100vh - 280px)" }}
        pagination={{
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "30", "50", "100"],
          onShowSizeChange: (current, size) => setPageSize(size),
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
      {!loading && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Alert
            type="info"
            message={
              <Typography.Text>
                <strong>NOTE :</strong>
                <> Only first 1000 records are being Displayed.</>
              </Typography.Text>
            }
            icon={<InfoCircleOutlined />}
            style={{ width: "fit-content", alignSelf: "flex-end" }}
            showIcon
          />
        </div>
      )}
    </div>
  );
}
